import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, CheckCircle2, Eye, ExternalLink, CalendarDays, Copy, AlertTriangle, Trash2, RefreshCw, CheckCircle, Upload, Split, BadgeCheck, ArrowRightLeft, Mail, Sparkles } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { resolveContaSicoob } from '../../src/lib/sicoobAccount';
import { resolveBancoGateway, cancelarCobrancaAsaas, prorrogarCobrancaAsaas, publicarPdfBoletoVigor, gerarBoletoGran, gerarPdfBoletoVigor } from '../../src/lib/gatewayResolver';
import { addDaysISO } from '../../src/lib/janelaCobranca';
import { Fatura } from '../../types';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { resolveCpfCnpjCliente } from '../../src/lib/cobrancaCliente';
import { marcarCobrancaComoPaga } from '../../src/services/api';
import { enviarCobrancaEmail } from '../../src/lib/cobrancaEmail';
import { hojeISO, montarConfirmacaoPagamentoManual, lerValorPago, valorDoBoleto, formatarValorPago } from '../../src/lib/confirmacaoPagamentoManual';
import { resolverLinkBoleto } from '../../src/lib/linkBoleto';
import { boletoExisteNoGateway, MOTIVO_CANCELAMENTO_LOCAL } from '../../src/lib/emissaoBoletoValida';
import { faltaNoMotivo, textoDoMotivoCancelamento } from '../../src/lib/motivosCancelamentoBoleto';
import { CampoMotivoCancelamento, SEM_MOTIVO_CANCELAMENTO, type MotivoCancelamentoEscolhido } from './components/CampoMotivoCancelamento';
import TimelineCobranca from './TimelineCobranca';
import { tipoDoEnvio, type TipoEnvio } from '../../src/lib/timelineCobranca';
import { boletoBaixadoNoBanco, type CancelamentoFila } from '../../src/lib/boletoBaixadoNoBanco';
import { divisaoBoletoParceira } from '../../src/lib/divisaoBoletoParceira';

// O topo dizia "a fatura e o boleto já foram enviados" também no mês sem boleto, em
// que só saiu a comunicação (pergunta do Ítalo sobre a UC 345243701247, 07/10/2026).
const RESUMO_DO_ENVIO: Record<TipoEnvio, string> = {
    boleto: 'A fatura e o boleto já foram enviados para o cliente.',
    comunicado: 'O cliente recebeu só a comunicação, sem cobrança: o cálculo deste mês foi pulado.',
    pulado: 'A cobrança foi marcada como enviada sem disparo: nada saiu para o cliente.',
};

interface CobrancaEnviadaModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura: Fatura | null;
    onUpdate?: () => void;
    onParcelar?: (fatura: Fatura) => void;
    onQuitar?: (fatura: Fatura) => void;
    onSincronizarStatus?: (fatura: Fatura) => void;
}

const CobrancaEnviadaModal: React.FC<CobrancaEnviadaModalProps> = ({ isOpen, onClose, fatura, onUpdate, onParcelar, onQuitar, onSincronizarStatus }) => {
    const backdropMouseDownRef = useRef(false);
    const [sendDate, setSendDate] = useState<Date | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [cancelConfirmation, setCancelConfirmation] = useState(false);
    // Motivo em lista fechada (#6509): o texto gravado começa pelo rótulo do motivo.
    const [motivoCancel, setMotivoCancel] = useState<MotivoCancelamentoEscolhido>(SEM_MOTIVO_CANCELAMENTO);
    const cancelReason = textoDoMotivoCancelamento(motivoCancel.codigo, motivoCancel.detalhe);
    const [prorrogarConfirmation, setProrrogarConfirmation] = useState(false);
    const [novaDataLimite, setNovaDataLimite] = useState('');
    const [alreadyPaidConfirmation, setAlreadyPaidConfirmation] = useState(false);
    const [paymentReceiptFile, setPaymentReceiptFile] = useState<File | null>(null);
    const [paymentDate, setPaymentDate] = useState(hojeISO());
    const [paymentValor, setPaymentValor] = useState('');
    const paymentValorLido = lerValorPago(paymentValor);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [pagandoEquatorial, setPagandoEquatorial] = useState(false);
    const [regenerandoPdf, setRegenerandoPdf] = useState(false);
    const [gerandoV6, setGerandoV6] = useState(false);
    const { addToast } = useToast();
    const { user, profile } = useAuth();

    // O gerenciamento de negociação de cobrança (status, follow-up, etc.) foi
    // consolidado só na tela "Vencidas há 15 dias" (Central de Inadimplência).
    // Aqui mantemos só a resolução do CPF/CNPJ, usada para sincronizar o status
    // "Pago" quando o boleto é confirmado manualmente (ver handleConfirmPaid).
    const [resolvedCpfCnpj, setResolvedCpfCnpj] = useState<string | null>(null);

    // Estado do envio por e-mail (Resend) — derivado do `fatura` recebido por
    // prop, mas guardado à parte: o objeto que o FinanceiroLayout passa para o
    // modal ABERTO nem sempre é recriado quando onUpdate() refaz o fetch, então
    // sem isto o card de "Reenviar e-mail" não trocaria de cor sozinho.
    const [emailCobranca, setEmailCobranca] = useState<{
        status: 'enviado' | 'falhou' | null;
        em: string | null;
        destinatarios: string[];
        anexos: string[];
        erro: string | null;
    } | null>(null);
    const [reenviandoEmail, setReenviandoEmail] = useState(false);

    // As telas de confirmação (já pago, cancelar, prorrogar) são da fatura em que foram
    // abertas. O modal fica montado entre uma fatura e outra, e o "Já pago" confirmado
    // fechava sem se desfazer: a próxima fatura abria nele, com o comprovante, a data e o
    // valor da anterior — um clique e ela era dada como paga com o recibo de outra.
    useEffect(() => {
        setCancelConfirmation(false);
        setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
        setProrrogarConfirmation(false);
        setNovaDataLimite('');
        setAlreadyPaidConfirmation(false);
        setPaymentReceiptFile(null);
        setPaymentDate(hojeISO());
        setPaymentValor('');
    }, [isOpen, fatura?.id]);

    // Boleto atual já baixado no Sicoob (#6124): a tela deixa de oferecer o que o
    // banco recusa e o "Cancelar boleto" libera a fatura sem chamar o banco de novo.
    const [filaCancelamento, setFilaCancelamento] = useState<CancelamentoFila[]>([]);
    useEffect(() => {
        setFilaCancelamento([]);
        if (!fatura?.id) return;
        let vivo = true;
        supabase
            .from('boleto_cancelamento_fila')
            .select('num_cobranca_boleto, conta_sicoob, status, processado_em')
            .eq('fatura_id', fatura.id)
            .eq('status', 'concluido')
            .then(({ data, error }) => {
                if (error) { console.error('[CobrancaEnviadaModal] fila de cancelamento', error); return; }
                if (vivo) setFilaCancelamento((data || []) as CancelamentoFila[]);
            });
        return () => { vivo = false; };
    }, [fatura?.id]);
    const baixaNoBanco = fatura ? boletoBaixadoNoBanco(fatura, filaCancelamento) : null;

    useEffect(() => {
        if (!fatura) { setEmailCobranca(null); return; }
        setEmailCobranca({
            status: fatura.email_cobranca_status ?? null,
            em: fatura.email_cobranca_em ?? null,
            destinatarios: fatura.email_cobranca_destinatarios ?? [],
            anexos: fatura.email_cobranca_anexos ?? [],
            erro: fatura.email_cobranca_erro ?? null,
        });
    }, [fatura?.id, fatura?.email_cobranca_em, fatura?.email_cobranca_status]);

    const handleReenviarEmail = async () => {
        if (!fatura) return;
        setReenviandoEmail(true);
        try {
            // Sem `destinatarios`: cai nos e-mails hoje cadastrados na UC — é o
            // comportamento certo para reenvio (o operador não está com a tela
            // de confirmação aberta para reeditar endereços).
            const resultado = await enviarCobrancaEmail(fatura.id, { forcar: true });
            if (resultado.ok) {
                const destinatariosEnviados = resultado.destinatarios || [];
                addToast(`E-mail reenviado para ${destinatariosEnviados.join(', ')}.`, 'success');
                setEmailCobranca({
                    status: 'enviado',
                    em: new Date().toISOString(),
                    destinatarios: destinatariosEnviados,
                    anexos: resultado.anexos || [],
                    erro: null,
                });
            } else {
                addToast(`Não foi possível reenviar o e-mail: ${resultado.mensagem}`, 'error');
            }
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'reenviar_cobranca_email', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'RESEND_COBRANCA_EMAIL', {
                fatura_id: fatura.id,
                ok: resultado.ok,
                motivo: resultado.ok ? null : resultado.motivo,
            });
        } finally {
            setReenviandoEmail(false);
            onUpdate?.();
        }
    };

    // Boleto único (Gran): quando existe o PDF combinado (layout da Gran + fatura da
    // Equatorial com marca d'água), ele é o que deve ser mostrado — não o link genérico
    // do Asaas. Qual das origens vale (site institucional, Storage, gateway) é
    // decisão única de resolverLinkBoleto.
    const handleVisualizarBoleto = async () => {
        let link = await resolverLinkBoleto(fatura);
        if (!link) {
            addToast('Erro ao gerar link do PDF do boleto.', 'error');
            return;
        }
        // Cache bust para garantir que o navegador baixe o PDF mais recente (caso tenha sido regerado)
        link = `${link}${link.includes('?') ? '&' : '?'}cb=${Date.now()}`;
        window.open(link, '_blank');
    };

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        addToast(`${label} copiado!`, 'success');
    };

    useEffect(() => {
        const fetchSendDate = async () => {
            if (!isOpen || !fatura) return;
            
            // If we already have the info in the fatura object, use it
            if (fatura.enviado_em) {
                setSendDate(new Date(fatura.enviado_em));
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                // Fetch the log describing when the boleto was sent to the client
                // using the logAction details.
                const { data, error } = await supabase
                    .from('system_logs')
                    .select('created_at')
                    .eq('action', 'SEND_BOLETO_CLIENT')
                    .contains('details', { fatura_id: fatura.id })
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .single();

                if (data && data.created_at) {
                    setSendDate(new Date(data.created_at));
                } else {
                    // Fallback to recent date if no log found
                    setSendDate(null);
                }
            } catch (error) {
                console.error('Error fetching send date:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSendDate();
    }, [isOpen, fatura]);

    // A timeline lê `enviado_em` da fatura; em fatura antiga esse carimbo não existe e
    // a data só sai do system_logs (resolvida acima, e só para quem é 'adm' — o RLS
    // da tabela é restrito). Sem esta ponte o evento de envio sumiria justo nas faturas
    // mais velhas, que são as que mais têm história para contar.
    const faturaComEnvio = useMemo(() => {
        if (!fatura) return null;
        if (fatura.enviado_em || !sendDate) return fatura;
        return { ...fatura, enviado_em: sendDate.toISOString() };
    }, [fatura, sendDate]);

    useEffect(() => {
        const resolve = async () => {
            if (!isOpen || !fatura) {
                setResolvedCpfCnpj(null);
                return;
            }
            const cpfCnpj = await resolveCpfCnpjCliente(fatura.uc_codigo, fatura.cliente_cpf_cnpj);
            setResolvedCpfCnpj(cpfCnpj || null);
        };
        resolve();
    }, [isOpen, fatura]);

    /**
     * Baixa o boleto no VigorHub: status, motivo, histórico e limpeza dos campos do
     * boleto. Usado pelo caminho Sicoob/n8n depois do cancelamento no banco e,
     * sozinho, quando não existe cobrança registrada para cancelar lá.
     * (O caminho Asaas não passa por aqui: a edge function já atualiza a fatura.)
     */
    const cancelarSomenteNoVigorHub = async () => {
        if (!fatura) return;
        const autor = profile?.full_name || user?.email || 'Sistema';
        const agora = new Date().toISOString();
        const newCancellation = {
                        // Qual boleto foi baixado. Sem isto o histórico registra quem, quando e por
            // quê, mas não O QUE — e como `num_cobranca_boleto` é zerado no mesmo update,
            // o número some da fatura por completo. Auditar boleto órfão virava inferência
            // por subtração entre `historico_emissoes` e a contagem de cancelamentos (foi
            // como o caso 11172 teve de ser levantado em 21/09/2026).
            num_cobranca_boleto: fatura.num_cobranca_boleto ?? null,
            cancelado_por_nome: autor,
            cancelado_em: agora,
            motivo_cancelamento_boleto: cancelReason.trim(),
            motivo_codigo: motivoCancel.codigo || null,
        };
        const updatedHistory = Array.isArray(fatura.historico_cancelamentos)
            ? [...fatura.historico_cancelamentos, newCancellation]
            : [newCancellation];

        const { error } = await supabase.from('faturas').update({
            status_boleto_vigor: 'Cancelado',
            status_fatura: 'Fatura validada',
            motivo_cancelamento_boleto: cancelReason.trim(),
            cancelado_por_nome: autor,
            cancelado_em: agora,
            historico_cancelamentos: updatedHistory,
            url_boleto: null,
            codigo_pix_boleto: null,
            linha_digitavel_boleto: null,
            num_cobranca_boleto: null,
            valor_consumo_scee_boleto: null,
            valor_adc_bandeira_boleto: null,
            valor_parc_injet_s_desc_boleto: null,
            valor_restituicao_imposto_boleto: null,
            valor_energia_nao_isenta_boleto: null,
            valor_boleto_vigor: null
        }).eq('id', fatura.id);

        // supabase-js não lança: sem checar, uma falha aqui deixava o boleto baixado no
        // Sicoob e a fatura viva na tela, com o operador vendo "cancelado".
        if (error) {
            throw new Error(
                'O boleto foi baixado, mas a fatura não pôde ser atualizada: ' + error.message
                + '. Recarregue a página e cancele de novo — a baixa já feita não se repete.'
            );
        }
    };

    const handleCancelBoleto = async () => {
        if (!fatura) return;

        const faltaMotivo = faltaNoMotivo(motivoCancel.codigo, motivoCancel.detalhe);
        if (faltaMotivo) {
            addToast(faltaMotivo, 'error');
            return;
        }

        setActionLoading(true);
        try {
            const gateway = await resolveBancoGateway(fatura.empresa_parceira_id);

            // Boleto que nunca foi registrado no gateway não tem o que baixar. Chamar o
            // Sicoob assim manda `nossoNumero: null`, o n8n monta `/boletos/null/baixar`
            // e o gateway devolve 400 "The request URI was invalid" — erro de URI, não de
            // negócio, que só deixava o operador sem conseguir cancelar (UC 346058301294,
            // fatura 9404 de JUL/2026). O cancelamento se resolve inteiro aqui.
            const semCobrancaNoGateway = !boletoExisteNoGateway(fatura);
            const jaEstaPago = fatura.status_boleto_vigor?.toLowerCase() === 'pago';

            if (semCobrancaNoGateway || jaEstaPago || baixaNoBanco) {
                await cancelarSomenteNoVigorHub();
                addToast(
                    jaEstaPago ? 'Pagamento desvinculado localmente com sucesso!'
                        : baixaNoBanco ? 'Este boleto já estava baixado no Sicoob: ele foi cancelado apenas no VigorHub. Se ainda precisa cobrar, emita um boleto novo por esta tela.'
                            : MOTIVO_CANCELAMENTO_LOCAL,
                    'success', 12000,
                );
            } else if (gateway === 'asaas') {
                // Empresa parceira com gateway Asaas — a própria edge function já atualiza
                // a fatura (zera campos, marca status_boleto_vigor, historico_cancelamentos).
                await cancelarCobrancaAsaas(fatura.id, cancelReason.trim());
                addToast('Boleto cancelado com sucesso!', 'success');
            } else {
                const { data: webhookData } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_cancellation_webhook_url')
                    .single();

                const webhookUrl = webhookData?.value;

                if (!webhookUrl) {
                    addToast('Webhook de cancelamento não configurado.', 'error');
                    return;
                }

                // A conta Sicoob PRECISA ir no corpo. O fluxo n8n "Baixa de boletos"
                // escolhe a conta só por `emitir_em_vigor_gestao`; sem o campo, cai na
                // Associação. As duas contas numeram boletos do zero, então o mesmo
                // nosso número existe nas duas: o boleto 163 da Gestão (fatura 10048,
                // UC 345243701247) não cancelava porque o pedido ia para o 163 da
                // Associação, de outro cliente e já pago — "em processo de liquidação ou
                // já liquidado" (#5987). Com o 163 da Associação em aberto, a tela teria
                // baixado o boleto de outro cliente e deixado este vivo. Mesma regra do
                // BoletoEmitidoModal e da fila (processar-fila-cancelamento-boleto).
                const contaSicoob = await resolveContaSicoob(fatura);

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: fatura.id,
                        nossoNumero: fatura.num_cobranca_boleto,
                        motivoCancelamento: cancelReason.trim(),
                        ...fatura,
                        emitir_em_vigor_gestao: contaSicoob === 'gestao',
                        conta_sicoob: contaSicoob,
                    })
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao cancelar (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }

                try {
                    const responseData = await response.json();

                    if (responseData?.status === 'erro') {
                        throw new Error(responseData.mensagem || 'Erro retornado pelo servidor.');
                    }

                    if (responseData?.mensagem) {
                        addToast(responseData.mensagem, 'success');
                    } else {
                        addToast('Solicitação de cancelamento processada!', 'success');
                    }
                } catch (e: any) {
                    if (e.message) throw e;
                    // If the response is not JSON
                    addToast('Solicitação de cancelamento processada!', 'success');
                }

                await cancelarSomenteNoVigorHub();
            }

            await logAction(user?.id || 'SYSTEM', 'CANCEL_BOLETO_WEBHOOK', { fatura_id: fatura.id, motivo: cancelReason.trim(), motivo_codigo: motivoCancel.codigo || null });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'cancelar_boleto', origem: 'acao' });

            setCancelConfirmation(false);
            setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
            onUpdate?.();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error.message || 'Erro ao cancelar boleto.', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegerarPdf = async () => {
        if (!fatura?.id) return;
        setRegenerandoPdf(true);
        try {
            if (!fatura.empresa_parceira_id) {
                await publicarPdfBoletoVigor(fatura.id);
                addToast('PDF regerado e publicado com sucesso.', 'success');
                await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                    fatura_id: fatura.id,
                    origem: 'vigor',
                });
            } else {
                const resultado = await gerarBoletoGran(fatura.id);
                if (resultado?.aviso) {
                    addToast(resultado.aviso, 'warning', 8000);
                } else {
                    addToast('PDF regerado. Abra "Ver boleto" para conferir.', 'success');
                }
                await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                    fatura_id: fatura.id,
                    origem: 'gran',
                });
            }
            if (onUpdate) onUpdate();
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'regerar_pdf_boleto', origem: 'acao' });
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setRegenerandoPdf(false);
        }
    };

    /** Abre numa aba esta fatura desenhada no layout novo do boleto unificado (v6).
     *  Mesmo botão do BoletoEmitidoModal — antes ele sumia assim que a cobrança era
     *  enviada. É só desenho: não toca na cobrança nem no PDF que o cliente recebeu. */
    const handleVerLayoutV6 = async () => {
        if (!fatura?.id) return;
        setGerandoV6(true);
        try {
            const blob = await gerarPdfBoletoVigor(fatura.id, 'v6');
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            // Revoga depois de um tempo: revogar na hora quebraria a aba recém-aberta.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            addToast('Boleto gerado no layout novo (v6).', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'regerar_pdf_boleto', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                fatura_id: fatura.id,
                origem: 'vigor',
                layout: 'v6',
            });
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setGerandoV6(false);
        }
    };

    const handleConfirmPaid = async () => {
        if (!fatura) return;
        if (!paymentReceiptFile) {
            addToast('O envio do comprovante é obrigatório.', 'error');
            return;
        }
        if (!paymentDate) {
            addToast('Informe a data em que o cliente pagou.', 'error');
            return;
        }
        if (paymentDate > hojeISO()) {
            addToast('A data do pagamento não pode ser no futuro.', 'error');
            return;
        }
        if (paymentValorLido == null) {
            addToast('Informe o valor pago, como está no comprovante.', 'error');
            return;
        }

        setPaymentLoading(true);
        try {
            // Upload receipt to Supabase storage
            const fileExt = paymentReceiptFile.name.split('.').pop();
            const fileName = `receipt-${fatura.id}-${Date.now()}.${fileExt}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('comprovantes')
                .upload(fileName, paymentReceiptFile);
            
            if (uploadError) {
                // Se o bucket não existir, o erro ajudará a identificar no console
                throw new Error('Falha ao enviar o comprovante: ' + uploadError.message);
            }

            const { data: publicUrlData } = supabase.storage
                .from('comprovantes')
                .getPublicUrl(fileName);

            const publicUrl = publicUrlData.publicUrl;

            // Grava a data informada pela equipe além do status — sem ela o boleto
            // fica "pago sem data" e vira falso vencido nos painéis (chamado #3789).
            // A guarda do boleto único vive no helper: ver o cabeçalho de
            // src/lib/confirmacaoPagamentoManual.ts.
            const { camposUpdate, ucBoletoUnico } = await montarConfirmacaoPagamentoManual(
                fatura.uc_codigo,
                publicUrl,
                paymentDate,
                paymentValorLido
            );

            const { error: updateError } = await supabase.from('faturas')
                .update(camposUpdate)
                .eq('id', fatura.id);

            if (updateError) throw updateError;

            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'confirmar_pagamento_manual', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'MANUAL_PAYMENT_CONFIRMATION', {
                fatura_id: fatura.id,
                comprovante_url: publicUrl,
                data_pagamento: paymentDate,
                valor_pago: paymentValorLido,
                data_gravada: !ucBoletoUnico,
            });

            if (ucBoletoUnico) {
                addToast('UC de boleto único: repasse à Equatorial segue pendente de ação manual.', 'info');
            }

            // Mantém o painel de negociação (status por cliente) em sincronia com a
            // confirmação de pagamento do boleto — os dois nunca devem divergir.
            if (resolvedCpfCnpj) {
                await marcarCobrancaComoPaga(
                    resolvedCpfCnpj,
                    user?.id || '',
                    profile?.full_name || user?.email || 'Sistema',
                    'Pagamento confirmado via comprovante anexado na fatura.'
                );
            }

            addToast('Pagamento confirmado com sucesso!', 'success');
            onUpdate?.();
            onClose();
        } catch (error: any) {
            console.error('Error confirming payment:', error);
            addToast(error.message || 'Erro ao confirmar pagamento.', 'error');
        } finally {
            setPaymentLoading(false);
        }
    };

    /**
     * Dispara o repasse à Equatorial pela RPC `reativar_repasse_equatorial`.
     *
     * Antes isto era um INSERT cru na fila, e não funcionava justamente no caso mais comum:
     * com um item parado em 'manual' o insert batia em `idx_peq_fatura_ativo` (o índice
     * único cobre 'manual') e o 23505 virava um toast informativo dizendo que já havia
     * pagamento em andamento — não havia; havia uma linha esperando um humano. O insert
     * também não gravava gateway/empresa_parceira_id/ambiente, então o item caía no caminho
     * Sicoob (default da coluna) e a edge function não sabia de qual conta Asaas pagar.
     *
     * A RPC promove o item parado em vez de duplicar, carimba a origem a partir da própria
     * fatura e é dona das validações — permissão, as duas guardas de pagamento em dobro
     * (débito automático e quitado pelo cliente), código de barras e webhook. Aqui só sobra
     * mostrar a mensagem que ela devolver.
     */
    const handlePagarEquatorialManual = async () => {
        if (!fatura) return;
        setPagandoEquatorial(true);
        try {
            const { error: rpcError } = await supabase
                .rpc('reativar_repasse_equatorial', { p_fatura_id: fatura.id });

            if (rpcError) throw new Error(rpcError.message);

            await logAction(user?.id || 'SYSTEM', 'PAGAR_EQUATORIAL_MANUAL', { fatura_id: fatura.id });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'pagar_equatorial_manual', origem: 'acao' });
            addToast('Pagamento à Equatorial enfileirado — processado em poucos minutos.', 'success');
            onUpdate?.();
        } catch (error: any) {
            console.error('Error enqueuing manual Equatorial payment:', error);
            addToast(error.message || 'Erro ao enfileirar pagamento da Equatorial.', 'error');
        } finally {
            setPagandoEquatorial(false);
        }
    };

    const openProrrogar = () => {
        if (!fatura) return;
        const atual = fatura.data_limite_pagamento_boleto || addDaysISO(fatura.vencimento_boleto, 30) || '';
        setNovaDataLimite(atual);
        setProrrogarConfirmation(true);
    };

    const handleProrrogarBoleto = async () => {
        if (!fatura) return;

        if (!fatura.num_cobranca_boleto) {
            addToast('Boleto sem nº de cobrança (nossoNumero). Não é possível prorrogar.', 'error');
            return;
        }
        if (!novaDataLimite) {
            addToast('Informe a nova data limite de pagamento.', 'error');
            return;
        }
        if (fatura.vencimento_boleto && novaDataLimite <= fatura.vencimento_boleto) {
            addToast('A nova data limite deve ser posterior ao vencimento do boleto.', 'error');
            return;
        }

        setActionLoading(true);
        try {
            const gateway = await resolveBancoGateway(fatura.empresa_parceira_id);

            if (gateway === 'asaas') {
                // A própria edge function já atualiza data_limite_pagamento_boleto na fatura.
                await prorrogarCobrancaAsaas(fatura.id, novaDataLimite);
                addToast('Prorrogação realizada com sucesso!', 'success');
            } else {
                const { data: webhookData } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_prorrogacao_webhook_url')
                    .single();

                const webhookUrl = webhookData?.value;

                if (!webhookUrl) {
                    addToast('Webhook de prorrogação não configurado.', 'error');
                    setActionLoading(false);
                    return;
                }

                const contaSicoob = await resolveContaSicoob(fatura);
                const emitirGestao = contaSicoob === 'gestao';

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: fatura.id,
                        nossoNumero: fatura.num_cobranca_boleto,
                        nova_data_limite_pagamento: novaDataLimite,
                        emitir_em_vigor_gestao: emitirGestao,
                        conta_sicoob: contaSicoob,
                    }),
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao acionar webhook (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }

                try {
                    const responseData = await response.json();
                    if (responseData?.status === 'erro') {
                        throw new Error(responseData.mensagem || 'Erro retornado pelo servidor.');
                    }
                    addToast(responseData?.mensagem || 'Prorrogação realizada com sucesso!', 'success');
                } catch (e: any) {
                    if (e.message) throw e;
                    addToast('Prorrogação realizada com sucesso!', 'success');
                }

                await supabase.from('faturas').update({
                    data_limite_pagamento_boleto: novaDataLimite,
                }).eq('id', fatura.id);
            }

            await logAction(user?.id || 'SYSTEM', 'PRORROGAR_BOLETO_WEBHOOK', {
                fatura_id: fatura.id,
                nova_data_limite_pagamento: novaDataLimite,
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'prorrogar_boleto', origem: 'acao' });

            setProrrogarConfirmation(false);
            onUpdate?.();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error.message || 'Erro ao prorrogar boleto.', 'error');
        } finally {
            setActionLoading(false);
        }
    };


    if (!isOpen || !fatura) return null;

    const divisaoPagamento = fatura.status_boleto_vigor?.toLowerCase() === 'pago'
        ? divisaoBoletoParceira(fatura)
        : null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
            style={{ zIndex: 9999 }}
            onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
            onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
        >

            {/* Cancel Confirmation */}
            {cancelConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Cancelar Boleto?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            {fatura?.status_boleto_vigor?.toLowerCase() === 'pago' ? (
                                <span className="text-amber-600 dark:text-amber-400 font-medium">Atenção: Este boleto consta como PAGO no sistema. O cancelamento será realizado apenas localmente no VigorHub (desvinculando o pagamento atual) para permitir uma nova emissão.</span>
                            ) : (
                                <>Uma solicitação de cancelamento será enviada. O status da fatura será atualizado para "Cancelado" se a operação for bem-sucedida no gateway.</>
                            )}
                        </p>
                        
                        <CampoMotivoCancelamento valor={motivoCancel} aoMudar={setMotivoCancel} />

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => {
                                    setCancelConfirmation(false);
                                    setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
                                }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleCancelBoleto}
                                disabled={actionLoading}
                                className="px-6 py-2.5 rounded-xl border-2 border-red-500 text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50"
                            >
                                {actionLoading ? 'Cancelando...' : 'Sim, cancelar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Prorrogar Confirmation */}
            {prorrogarConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CalendarDays size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Prorrogar data limite de pagamento</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Estende o prazo de pagamento do boleto <strong>sem reemitir</strong> (sem nova tarifa de emissão). O vencimento e os encargos (multa/juros) seguem a partir do vencimento original. Só funciona enquanto o boleto está "Em aberto" no Sicoob.
                        </p>

                        <div className="mb-6 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Nova data limite de pagamento <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={novaDataLimite}
                                min={fatura.data_limite_pagamento_boleto || fatura.vencimento_boleto || undefined}
                                onChange={(e) => setNovaDataLimite(e.target.value)}
                                className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                            />
                            {fatura.data_limite_pagamento_boleto && (
                                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Limite atual: {fatura.data_limite_pagamento_boleto.split('-').reverse().join('/')}</p>
                            )}
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setProrrogarConfirmation(false)}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleProrrogarBoleto}
                                disabled={actionLoading}
                                className="px-6 py-2.5 rounded-xl border-2 border-amber-500 text-amber-600 font-bold hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors disabled:opacity-50"
                            >
                                {actionLoading ? 'Prorrogando...' : 'Confirmar prorrogação'}
                            </button>
                        </div>
                    </div>
                </div>
            )}



            {/* Already Paid Confirmation */}
            {alreadyPaidConfirmation && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Já foi paga?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6 font-medium">
                            Para confirmar que essa cobrança já foi paga, informe a data e o valor do pagamento e envie o comprovante.
                        </p>

                        <div className="mb-5 text-left">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Data do Pagamento *
                            </label>
                            <input
                                type="date"
                                value={paymentDate}
                                max={hojeISO()}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                disabled={paymentLoading}
                                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <p className="text-[11px] text-gray-400 mt-1.5">
                                A data que consta no comprovante — não a de hoje, se o cliente pagou antes.
                            </p>
                        </div>

                        <div className="mb-5 text-left">
                            <label htmlFor="valor-pago-cobranca" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Valor Pago *
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">R$</span>
                                <input
                                    id="valor-pago-cobranca"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder="0,00"
                                    value={paymentValor}
                                    onChange={(e) => setPaymentValor(e.target.value)}
                                    disabled={paymentLoading}
                                    aria-invalid={paymentValor !== '' && paymentValorLido == null}
                                    className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                            </div>
                            {paymentValor !== '' && paymentValorLido == null ? (
                                <p className="text-[11px] font-semibold text-red-500 mt-1.5">
                                    Valor inválido. Digite só o número, por exemplo 1.234,56.
                                </p>
                            ) : (
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    Já vem com o valor do boleto. Confira no comprovante e corrija se o cliente pagou outro valor.
                                </p>
                            )}
                        </div>

                        <div className="mb-6 text-left relative">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Comprovante de Pagamento *
                            </label>
                            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${paymentReceiptFile ? 'border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Upload className={`w-8 h-8 mb-3 ${paymentReceiptFile ? 'text-primary' : 'text-gray-400'}`} />
                                    {paymentReceiptFile ? (
                                        <p className="text-sm font-bold text-primary text-center px-4 truncate w-full">
                                            {paymentReceiptFile.name}
                                        </p>
                                    ) : (
                                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="font-semibold">Clique para anexar</span> ou arraste o arquivo
                                        </p>
                                    )}
                                </div>
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    accept="image/*,.pdf" 
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setPaymentReceiptFile(e.target.files[0]);
                                        }
                                    }} 
                                    disabled={paymentLoading}
                                />
                            </label>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => {
                                    setAlreadyPaidConfirmation(false);
                                    setPaymentReceiptFile(null);
                                    setPaymentDate(hojeISO());
                                    setPaymentValor('');
                                }}
                                disabled={paymentLoading}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmPaid}
                                disabled={paymentLoading || !paymentReceiptFile || !paymentDate || paymentValorLido == null}
                                className="px-6 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {paymentLoading ? 'Confirmando...' : 'Confirmar Pagamento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div
                className={`bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border border-emerald-100 dark:border-emerald-900/30 ${(cancelConfirmation || prorrogarConfirmation || alreadyPaidConfirmation) ? 'opacity-0 pointer-events-none' : ''}`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-8 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-emerald-50/50 dark:bg-emerald-900/10 sticky top-0 z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <CheckCircle2 className="text-emerald-500" size={28} />
                            Cobrança Enviada
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-10">
                            {fatura.cliente_nome} • {fatura.uc_codigo} • {fatura.mes_referencia}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400">
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 bg-white dark:bg-gray-800 py-8 min-h-[300px] flex flex-col items-center overflow-y-auto">

                    <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mb-6 shadow-sm">
                        <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">Tudo certo por aqui!</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-xl text-center">
                        {RESUMO_DO_ENVIO[tipoDoEnvio(faturaComEnvio ?? {}) ?? 'boleto']} Você pode visualizar os documentos consultando os links abaixo.
                    </p>

                    {/* Histórico da cobrança — emissão, cancelamento, reemissão, envio.
                        Antes eram dois cards planos ("Emitido por" / "Enviado por"), os dois
                        preenchidos com quem mexeu por ÚLTIMO: quem emitiu e teve o boleto
                        cancelado depois sumia da tela (chamado do Ítalo, 28/08/2026).
                        `sendDate` entra no lugar de `enviado_em` porque em fatura antiga o
                        carimbo só existe no system_logs, resolvido no efeito acima. */}
                    <div className="w-full max-w-3xl mb-8">
                        {loading ? (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700 space-y-3">
                                <div className="h-3 w-40 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                                <div className="h-5 w-64 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                                <div className="h-5 w-56 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                            </div>
                        ) : (
                            <TimelineCobranca fatura={faturaComEnvio} />
                        )}
                    </div>

                    {/* Divisão do pagamento — fatura PAGA de empresa parceira. Os valores
                        vêm do split que foi de fato para o Asaas (divisaoBoletoParceira),
                        não da divisão teórica de energia: conferido contra o extrato nas
                        faturas pagas da Gran em 01/10/2026. */}
                    {divisaoPagamento && (
                        <div className="w-full max-w-3xl mb-8">
                            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
                                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                    <Split size={12} /> Divisão do pagamento
                                </p>
                                <div className={`grid grid-cols-2 ${divisaoPagamento.equatorial > 0 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-3`}>
                                    {[
                                        { rotulo: 'Cliente pagou', valor: divisaoPagamento.boleto, cor: 'text-gray-500 dark:text-gray-400', dica: 'Valor do boleto' },
                                        { rotulo: 'Vigor', valor: divisaoPagamento.vigor, cor: 'text-emerald-600 dark:text-emerald-400', dica: divisaoPagamento.emissora === 'parceira' ? 'Split que a conta da parceira mandou para a Vigor' : 'O que ficou na conta da Vigor depois do split da parceira' },
                                        { rotulo: 'Parceira', valor: divisaoPagamento.parceira, cor: 'text-amber-600 dark:text-amber-400', dica: divisaoPagamento.emissora === 'vigor' ? 'Split que a conta da Vigor mandou para a parceira' : 'O que ficou na conta da parceira depois do split da Vigor e da Equatorial' },
                                        ...(divisaoPagamento.equatorial > 0
                                            ? [{ rotulo: 'Equatorial', valor: divisaoPagamento.equatorial, cor: 'text-sky-600 dark:text-sky-400', dica: `Conta de luz embutida no boleto, paga pela conta da ${divisaoPagamento.emissora === 'vigor' ? 'Vigor' : 'parceira'}` }]
                                            : []),
                                    ].map(({ rotulo, valor, cor, dica }) => (
                                        <div key={rotulo} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700" title={dica}>
                                            <p className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${cor}`}>{rotulo}</p>
                                            <p className={`text-sm font-montserrat font-bold ${valor < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                                                {valor < 0 ? '−' : ''}R$ {Math.abs(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
                                    Boleto emitido na conta da {divisaoPagamento.emissora === 'vigor' ? 'Vigor' : 'parceira'}: a taxa do Asaas sai de lá e não está descontada da parte {divisaoPagamento.emissora === 'vigor' ? 'da Vigor' : 'da parceira'}.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Envio por e-mail (Resend) — só aparece se já houve alguma tentativa
                        (email_cobranca_status). Faturas sem tentativa (UC sem opt-in, ou
                        anteriores a esta feature) não mostram nada aqui. */}
                    {!baixaNoBanco && emailCobranca?.status === 'enviado' && (
                        <div className="w-full max-w-3xl bg-emerald-50 dark:bg-emerald-900/10 rounded-xl p-4 border border-emerald-100 dark:border-emerald-900/30 mb-8 flex items-start gap-4">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm shrink-0">
                                <Mail className="text-emerald-600 dark:text-emerald-400" size={24} />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-1">
                                    E-mail enviado{emailCobranca.em ? ` em ${new Date(emailCobranca.em).toLocaleDateString('pt-BR')} às ${new Date(emailCobranca.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </p>
                                {emailCobranca.destinatarios.length > 0 && (
                                    <p className="text-xs text-gray-600 dark:text-gray-300 font-mono break-all">
                                        Para: {emailCobranca.destinatarios.join(', ')}
                                    </p>
                                )}
                                {emailCobranca.anexos.length < 2 && (
                                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                                        {emailCobranca.anexos.some(a => a.startsWith('boleto-'))
                                            ? 'Sem o PDF da fatura da distribuidora.'
                                            : emailCobranca.anexos.some(a => a.startsWith('fatura-distribuidora-'))
                                                ? 'Sem o PDF do boleto.'
                                                : 'Sem anexos — enviado só com PIX/linha digitável no corpo.'}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={handleReenviarEmail}
                                disabled={reenviandoEmail}
                                className="text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:underline shrink-0 disabled:opacity-50 flex items-center gap-1.5"
                            >
                                <RefreshCw size={12} className={reenviandoEmail ? 'animate-spin' : ''} />
                                {reenviandoEmail ? 'Reenviando...' : 'Reenviar'}
                            </button>
                        </div>
                    )}

                    {!baixaNoBanco && emailCobranca?.status === 'falhou' && (
                        <div className="w-full max-w-3xl bg-red-50 dark:bg-red-900/10 rounded-xl p-4 border border-red-100 dark:border-red-900/30 mb-8 flex items-start gap-4">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm shrink-0">
                                <AlertTriangle className="text-red-500" size={24} />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wider mb-1">
                                    Falha ao enviar o e-mail{emailCobranca.em ? ` — tentado em ${new Date(emailCobranca.em).toLocaleDateString('pt-BR')} às ${new Date(emailCobranca.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    {emailCobranca.erro || 'Motivo não registrado.'}
                                </p>
                            </div>
                            <button
                                onClick={handleReenviarEmail}
                                disabled={reenviandoEmail}
                                className="text-red-600 bg-white dark:bg-gray-800 hover:bg-red-100 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg font-bold text-xs transition-colors flex items-center gap-1.5 border border-red-200 dark:border-red-900/40 shrink-0 disabled:opacity-50"
                            >
                                <RefreshCw size={14} className={reenviandoEmail ? 'animate-spin' : ''} />
                                {reenviandoEmail ? 'Reenviando...' : 'Reenviar e-mail'}
                            </button>
                        </div>
                    )}

                    {baixaNoBanco && (
                        <div role="alert" className="w-full max-w-3xl bg-red-50 dark:bg-red-900/10 rounded-xl p-4 border border-red-200 dark:border-red-900/40 mb-6 flex items-start gap-4">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm shrink-0">
                                <AlertTriangle className="text-red-500" size={24} />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-bold text-red-700 dark:text-red-400 mb-1">
                                    Boleto {fatura.num_cobranca_boleto} já foi baixado no banco
                                    {baixaNoBanco.processado_em ? ` (registrado em ${new Date(baixaNoBanco.processado_em).toLocaleDateString('pt-BR')})` : ''}
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    O cliente não consegue pagar este boleto, e a fatura continua em aberto. Não envie o boleto, o PIX ou a linha digitável.
                                    Para cobrar, clique em <strong>Cancelar boleto</strong> (libera a fatura sem chamar o banco) e emita um novo.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Links */}
                    <div className="flex flex-col sm:flex-row gap-4 w-full max-w-3xl justify-center mt-2">
                        {fatura.pdf_fatura && (
                            <button
                                onClick={() => window.open(fatura.pdf_fatura, '_blank')}
                                className="flex-1 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-secondary text-gray-700 dark:text-gray-200 hover:text-secondary px-6 py-4 rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 font-bold group"
                            >
                                <Eye size={20} className="group-hover:scale-110 transition-transform" />
                                Ver fatura
                            </button>
                        )}
                        {!baixaNoBanco && (fatura.url_boleto || fatura.pdf_boleto_combinado) && (
                            <button
                                onClick={handleVisualizarBoleto}
                                className="flex-1 bg-primary/10 dark:bg-primary/20 border border-transparent hover:border-primary text-primary px-6 py-4 rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 font-bold group"
                            >
                                <ExternalLink size={20} className="group-hover:scale-110 transition-transform" />
                                Ver boleto
                            </button>
                        )}
                        {!!fatura?.id && (
                            <button
                                onClick={handleRegerarPdf}
                                disabled={regenerandoPdf}
                                title="Redesenha o PDF e sobrescreve no Storage. Não altera a cobrança no banco/gateway."
                                className="flex-1 bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary text-gray-500 dark:text-gray-400 hover:text-primary px-6 py-4 rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 font-bold group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RefreshCw size={20} className={regenerandoPdf ? 'animate-spin text-primary' : 'group-hover:rotate-180 transition-transform'} />
                                {regenerandoPdf ? 'Gerando...' : 'Regerar e Substituir PDF'}
                            </button>
                        )}
                    </div>

                    {/* Layout novo do boleto unificado — mesma regra do BoletoEmitidoModal: só adm,
                        só fatura da Vigor com boleto emitido. A Gran tem PDF próprio (layout da Gran
                        + fatura da Equatorial), e sem boleto o v6 sairia sem PIX nem código de barras. */}
                    {!baixaNoBanco && profile?.role === 'adm' && !!fatura.url_boleto && !fatura.empresa_parceira_id && (
                        <button
                            onClick={handleVerLayoutV6}
                            disabled={gerandoV6}
                            title="Desenha esta fatura no layout novo do boleto unificado e abre numa nova aba. Só visualização — não altera a cobrança nem o PDF enviado ao cliente."
                            className="w-full max-w-3xl mt-4 bg-white dark:bg-gray-800 border border-dashed border-emerald-300 dark:border-emerald-800 hover:border-emerald-500 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 px-6 py-2.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Sparkles size={14} className={gerandoV6 ? 'animate-pulse' : ''} />
                            {gerandoV6 ? 'Gerando PDF...' : 'Ver no layout novo (v6)'}
                        </button>
                    )}

                    {/* Copy Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-3xl justify-center mt-6">
                        {!baixaNoBanco && fatura.codigo_pix_boleto && (
                            <div className="flex flex-col gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 -mx-2 rounded-xl transition-colors">
                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Código PIX (Copia e Cola)</span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={fatura.codigo_pix_boleto}
                                        className="flex-1 min-w-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                                    />
                                    <button
                                        onClick={() => handleCopy(fatura.codigo_pix_boleto!, 'Código PIX')}
                                        className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        title="Copiar PIX"
                                    >
                                        <Copy size={18} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {!baixaNoBanco && fatura.linha_digitavel_boleto && (
                            <div className="flex flex-col gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 -mx-2 rounded-xl transition-colors">
                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Linha Digitável do Boleto</span>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        readOnly
                                        value={fatura.linha_digitavel_boleto}
                                        className="flex-1 min-w-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                                    />
                                    <button
                                        onClick={() => handleCopy(fatura.linha_digitavel_boleto!, 'Linha Digitável')}
                                        className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        title="Copiar Linha Digitável"
                                    >
                                        <Copy size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Boleto Único — status do repasse à Equatorial */}
                    {fatura.status_repasse_equatorial && (
                        <div className={`mt-4 w-full max-w-3xl mx-auto flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-bold ${
                            fatura.status_repasse_equatorial === 'pago'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800'
                                : fatura.status_repasse_equatorial === 'falhou' || fatura.status_repasse_equatorial === 'dados_indisponiveis'
                                    ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
                                    : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800'
                        }`}>
                            <ArrowRightLeft size={16} />
                            {/* Espelha o CHECK faturas_status_repasse_equatorial_check. Status
                                sem texto aqui aparecia como frase cortada, sem dizer nada. */}
                            Repasse Equatorial (boleto único): {{
                                pago: 'pago com sucesso',
                                pendente: 'pendente de processamento',
                                aguardando_confirmacao: 'pagamento enviado — aguardando confirmação',
                                manual_pendente: 'a empresa parceira paga por fora — baixa manual',
                                falhou: 'falhou — pague manualmente',
                                dados_indisponiveis: 'sem linha digitável/PIX da Equatorial — pague manualmente',
                                reembolsado: 'reembolsado',
                                uc_nao_encontrada: 'UC não encontrada — confira o cadastro',
                                debito_automatico: 'não há repasse: a Equatorial debita direto na conta do cliente',
                                quitado_pelo_cliente: 'não há repasse: o cliente quitou a Equatorial e o boleto cobrou só a Vigor',
                                nao_aplicavel: 'não aplicável',
                            }[fatura.status_repasse_equatorial]}
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-6 bg-white dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0 backdrop-blur-md">
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        <button
                            onClick={() => setCancelConfirmation(true)}
                            disabled={actionLoading}
                            className="text-red-500 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-red-100 dark:border-red-900/30 w-full sm:w-auto justify-center"
                        >
                            <X size={16} /> Cancelar boleto
                        </button>
                        {!baixaNoBanco && ['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && fatura.num_cobranca_boleto && (
                            <button
                                onClick={openProrrogar}
                                disabled={actionLoading}
                                className="text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-amber-100 dark:border-amber-900/30 w-full sm:w-auto justify-center"
                            >
                                <CalendarDays size={16} /> Prorrogar boleto
                            </button>
                        )}
                        {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onParcelar && (
                            <button
                                onClick={() => onParcelar(fatura)}
                                disabled={actionLoading}
                                className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/10 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-indigo-100 dark:border-indigo-900/30 w-full sm:w-auto justify-center"
                            >
                                <Split size={16} /> Parcelar
                            </button>
                        )}
                        {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onQuitar && (
                            <button
                                onClick={() => onQuitar(fatura)}
                                disabled={actionLoading}
                                className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100 dark:hover:bg-teal-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-teal-100 dark:border-teal-900/30 w-full sm:w-auto justify-center"
                            >
                                <BadgeCheck size={16} /> Quitar
                            </button>
                        )}
                        {/* Este é o único caminho para "Sincronizar status" na visão de
                            Kanban: lá o card não tem menu "..." — clicar abre este modal.
                            Ver docs/superpowers/specs/2026-09-02-sincronizacao-status-fatura-design.md */}
                        {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onSincronizarStatus && (
                            <button
                                onClick={() => onSincronizarStatus(fatura)}
                                disabled={actionLoading}
                                className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100 dark:hover:bg-teal-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-teal-100 dark:border-teal-900/30 w-full sm:w-auto justify-center"
                            >
                                <RefreshCw size={16} /> Sincronizar
                            </button>
                        )}
                        {/* Nada a pagar quando a conta já está paga — por débito automático da
                            distribuidora ou porque o cliente quitou por fora (aí o boleto nem
                            cobrou essa parte). Oferecer o botão nesses casos é oferecer o
                            pagamento em dobro, que é exatamente o erro que essas duas guardas
                            existem para evitar. */}
                        {fatura.status_repasse_equatorial
                            && !['pago', 'debito_automatico', 'quitado_pelo_cliente'].includes(fatura.status_repasse_equatorial) && (
                            <button
                                onClick={handlePagarEquatorialManual}
                                disabled={pagandoEquatorial}
                                className="text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 hover:bg-orange-100 dark:hover:bg-orange-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-orange-100 dark:border-orange-900/30 w-full sm:w-auto justify-center disabled:opacity-60"
                            >
                                {pagandoEquatorial ? <RefreshCw size={16} className="animate-spin" /> : <ArrowRightLeft size={16} />}
                                Pagar Equatorial agora
                            </button>
                        )}
                    </div>
                    {fatura.status_boleto_vigor?.toLowerCase() !== 'pago' && (
                        <div className="flex items-center gap-3 w-full sm:w-auto mt-4 sm:mt-0">
                            <button
                                onClick={() => {
                                    setPaymentValor(formatarValorPago(valorDoBoleto(fatura)));
                                    setAlreadyPaidConfirmation(true);
                                }}
                                disabled={actionLoading}
                                className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-5 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                            >
                                <CheckCircle2 size={18} /> Essa cobrança já foi paga?
                            </button>
                        </div>
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
};

export default CobrancaEnviadaModal;
